import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Coredata, DateRangeInterface, Service } from 'webapp-types';
import {
	DateRangeSelectionId,
	DateRangeTypeId,
	GranulationTypeId,
	TimeUnit,
} from 'webapp/enums/enums';
import { copyObject, equal, generateNormalizedString, uniq } from 'webapp/utils/common';
import { getDateRangeIdFromFormula } from 'webapp/utils/date-range';
import { getFormattedPeriod, hashCode } from 'webapp/utils/number';

import type MetricLimitationRange from 'webapp/models-custom/metric-limitation/range';
import Serviced from 'webapp/models-custom/serviced';

const RangeCategory = {
	ALLTIME: 1,
	CURRENT: 2,
	PREVIOUS: 3,
	NEXT: 4,
	CUSTOM: 5,
	POPULAR: 6,
};

interface Granulation {
	id: GranulationTypeId;
	title: string;
	unit: number;
	formula: DateRangeInterface.GranulationInterface;
}

interface PredefinedRangeFormulaInterface {
	formula: DateRangeInterface.RangeInterface;
}

interface PredefinedGranulationFormulaInterface {
	formula: DateRangeInterface.GranulationInterface;
}

export default class DateRange extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('logger') declare loggerService: Service.Logger;

	@tracked limitations: MetricLimitationRange[] = [];

	// ----------- Range props -----------

	@tracked rangeCategoryId = RangeCategory.POPULAR; // one of RangeCategory

	@tracked rangeTimeUnit: TimeUnit | null = null;

	@tracked rangeMultiplier: number | null = null;

	@tracked rangeOffset: number | null = null;

	@tracked isAllTime = false; // AllTime only

	@tracked rangeIncludeFuture = false; // today only

	@tracked rangeStartDate: string | null = null; // fixed + since only

	@tracked rangeEndDate: string | null = null; // fixed only

	@tracked isRolling = false;

	@tracked rangeTypeIdVariations: DateRangeTypeId[] = [];

	// rangeIncludeCurrent - last x only
	get rangeIncludeCurrent(): boolean {
		return this.rangeOffset === 0 && (this.isLastX || this.isSince);
	}

	set rangeIncludeCurrent(value: boolean) {
		this.updatePropIfChanged('rangeOffset', value ? 0 : -1);
	}

	// only used to determine which rangeType to use for limitations
	@cached
	get rangeTypeId(): DateRangeTypeId | '' {
		// find BE-set date range type id that will be used for reading metric limitations
		let rangeTypeId;

		const formula = this._getRangeFormula();

		if (formula.isAllTime) {
			rangeTypeId = DateRangeTypeId.ALL_TIME;
		} else {
			const matchIds = (Object.keys(DateRange.rangeFormulas) as DateRangeTypeId[]).filter(
				(typeId) => {
					const typeDef = DateRange.getPredefinedRangeFormula(typeId);
					return (
						typeDef.timeUnit === formula.timeUnit &&
						!typeDef.includeFuture === !formula.includeFuture &&
						!typeDef.isAllTime === !formula.isAllTime &&
						(typeof typeDef.offset !== 'number' || typeDef.offset === formula.offset) &&
						(typeof typeDef.multiplier !== 'number' ||
							typeDef.multiplier === formula.multiplier) &&
						!typeDef.startDate === !formula.startDate
					);
				},
			);

			if (matchIds.length > 1) {
				const match = matchIds.find((mId) => {
					const typeDef = DateRange.getPredefinedRangeFormula(mId);
					return (
						typeof typeDef.offset === 'number' && typeof typeDef.multiplier === 'number'
					);
				});
				if (match) {
					rangeTypeId = match;
				}
			}

			if (!rangeTypeId) {
				rangeTypeId = matchIds[0];
			}

			if (!rangeTypeId) {
				if (formula.startDate && formula.endDate) {
					rangeTypeId = DateRangeTypeId.CUSTOM;
				} else if (formula.startDate) {
					rangeTypeId =
						formula.offset === -1
							? DateRangeTypeId.SINCE_TO_DATE_WOC
							: DateRangeTypeId.SINCE_TO_DATE;
				} else if (
					formula.timeUnit === TimeUnit.MONTH &&
					formula.offset &&
					formula.offset > 1 &&
					formula.multiplier === formula.offset
				) {
					// this is a "NextXMonths", currently supported only on forecasts
					// the limitations used should be the same as for "LastXMonths"
					rangeTypeId = DateRangeTypeId.LAST_X_MONTHS;
				}
			}
		}

		if (!rangeTypeId && !DateRange.isRangeFormulaEmpty(formula) && !this.isNextXMonths) {
			this.loggerService.logError('[CDR] rangeTypeId not found', {
				formula: JSON.stringify(formula),
			});
		}

		return rangeTypeId || '';
	}

	get isNextXMonths() {
		// this is currently supported only on forecasts
		// if we need it anywhere else, consider adding a proper limitation
		// that is also returned on BE side
		const rangeOffset = this.rangeOffset!,
			rangeMultiplier = this.rangeMultiplier!;
		return (
			this.rangeTimeUnit === TimeUnit.MONTH &&
			(rangeOffset > 1 || (rangeOffset === 1 && rangeMultiplier === 2)) &&
			(rangeMultiplier === rangeOffset || rangeMultiplier === rangeOffset + 1) // include current vs not
		);
	}

	// only used to determine which item is chosen on the predefined list on UI
	@cached
	get rangeUISelectionId(): DateRangeSelectionId {
		let id = '';

		const rangeTypeId = this.rangeTypeId || '';

		if (rangeTypeId.includes('Rolling') || (this.rangeStartDate && this.rangeEndDate)) {
			id = DateRangeSelectionId.CUSTOM;
		} else if (this.isAllTime) {
			id = DateRangeSelectionId.ALL_TIME;
		} else if (Math.abs(this.rangeOffset!) > 1) {
			if (this.rangeTimeUnit === TimeUnit.DAY) {
				// rolling
				id = DateRangeSelectionId.CUSTOM;
			}
		} else {
			if (rangeTypeId.startsWith('LastX')) {
				id = DateRangeSelectionId.LAST_X;
			} else if (rangeTypeId.startsWith('Today')) {
				id = DateRangeSelectionId.TODAY;
			} else if (rangeTypeId.startsWith('SinceToDate')) {
				id = DateRangeSelectionId.SINCE_TO_DATE;
			} else if (rangeTypeId) {
				// the rest must match
				id = rangeTypeId;
			}
		}

		return id as DateRangeSelectionId;
	}

	get isToday(): boolean {
		return this.rangeUISelectionId === DateRangeSelectionId.TODAY;
	}

	get isYesterday(): boolean {
		return (
			this.rangeTimeUnit === TimeUnit.DAY &&
			this.rangeMultiplier === 1 &&
			this.rangeOffset === -1
		);
	}

	get isLastX(): boolean {
		return this.rangeUISelectionId === DateRangeSelectionId.LAST_X;
	}

	get isSince(): boolean {
		return this.rangeUISelectionId === DateRangeSelectionId.SINCE_TO_DATE;
	}

	get isCustom(): boolean {
		// "custom" on UI - also includes rolling
		return this.rangeUISelectionId === DateRangeSelectionId.CUSTOM;
	}

	get isEmpty(): boolean {
		return DateRange.isRangeFormulaEmpty(this.rangeFormula);
	}

	private _rangeTitle = '';

	get rangeTitle(): string {
		let title = this._rangeTitle;
		if (!title) {
			// if this is not predefined, then we find the predefined that matches and get their title
			const rangeFormula = this.rangeFormula,
				rangeFormulaSorted = JSON.stringify(
					rangeFormula,
					Object.keys(rangeFormula || {}).sort(),
				);
			DateRange.predefinedRanges.some((range) => {
				const predefinedFormula = range.rangeFormula,
					predefinedFormulaSorted = JSON.stringify(
						predefinedFormula,
						Object.keys(predefinedFormula || {}).sort(),
					);
				if (rangeFormulaSorted === predefinedFormulaSorted) {
					title = range.rangeTitle;
					return true;
				}
				return false;
			});
			if (!title) {
				DateRange.predefinedRanges.some((range) => {
					if (
						this.rangeTypeId &&
						range.rangeTypeIdVariations.includes(this.rangeTypeId)
					) {
						title = range.rangeTitle;
						return true;
					}
					return false;
				});
			}
			if (title) {
				this._rangeTitle = title;
			}
		}
		return title;
	}

	set rangeTitle(value: string) {
		// setting only on predefined date ranges or the copies created from them
		this._rangeTitle = value;
	}

	get rangeTitleUX(): string {
		let title;
		if (this.isLastX || this.isNextXMonths) {
			let unitString = DateRange.getTimeUnitText(this.rangeTimeUnit as number);
			if (this.rangeMultiplier !== 1) {
				unitString += 's';
			}
			const prefix = this.rangeOffset && this.rangeOffset > 0 ? 'Next' : 'Last';
			title = `${prefix} ${this.rangeMultiplier} ${unitString}`;
		} else if (this.isSince) {
			title = getFormattedPeriod(
				moment(this.rangeStartDate, DateRange.customDateFormat).unix(),
				moment().unix(),
				null,
				this.accountService.accountDateFormatHumanReadable,
				this.accountService.accountDateFormatHumanReadableWithoutYear,
			);
			title = `Since ${title.split(' - ')[0]}`;
		} else if (this.isCustom) {
			title = getFormattedPeriod(
				moment(this.rangeStartDate, DateRange.customDateFormat).unix(),
				moment(this.rangeEndDate, DateRange.customDateFormat).unix(),
				null,
				this.accountService.accountDateFormatHumanReadable,
				this.accountService.accountDateFormatHumanReadableWithoutYear,
			);
			if (this.isRolling) {
				title += ' (rolling)';
			}
		} else {
			title = this.rangeTitle;
		}
		return title;
	}

	get rangeTitleShort(): string {
		let title = this.rangeTitleUX;
		if (this.isCustom) {
			title = title = getFormattedPeriod(
				moment(this.rangeStartDate, DateRange.customDateFormat).unix(),
				moment(this.rangeEndDate, DateRange.customDateFormat).unix(),
				null,
				this.accountService.accountDateFormat,
				this.accountService.accountDateFormatWithoutYear,
			);
		}
		return title;
	}

	get granulationTitleUX(): string {
		return (
			DateRange.predefinedGranulations.find((item) => item.id === this.granulationTypeId)
				?.title || this.granulationTypeId
		);
	}

	get hasExtraSettings(): boolean {
		return this.isToday || this.isLastX || this.isSince || this.isCustom;
	}

	get normalizedTitleUX(): string {
		return generateNormalizedString(this.rangeTitleUX);
	}

	// ----------- Granulation props -----------

	// used to determine which rangeType to use for limitations
	// also is 1:1 with IDs from predefinedGranulations
	@cached
	get granulationTypeId(): GranulationTypeId {
		// find BE-set granulation type id that will be used for reading metric limitations
		const formula = this.granulationFormula;

		const matchIds = (Object.keys(DateRange.granulationFormulas) as GranulationTypeId[]).filter(
			(typeId) => {
				const typeDef = DateRange.getPredefinedGranulationFormula(typeId);
				return (
					typeDef.isWholeRange === formula.isWholeRange &&
					typeDef.timeUnit === formula.timeUnit &&
					typeDef.multiplier === formula.multiplier
				);
			},
		);

		if (matchIds.length > 1) {
			this.loggerService.logError('[CDR] granulationTypeId found multiple matches', {
				formula: JSON.stringify(formula),
			});
		}

		const granulationTypeId = matchIds[0];

		if (!granulationTypeId) {
			this.loggerService.logError('[CDR] granulationTypeId not found', {
				formula: JSON.stringify(formula),
			});
		}

		return granulationTypeId!;
	}

	@tracked granulationTimeUnit: TimeUnit | null = null;
	@tracked granulationMultiplier: number | null = null;
	@tracked granulationIsWholeRange = false;

	@tracked granulationCumulative = false;

	get isGranulationTotal(): boolean {
		return this.granulationTypeId === GranulationTypeId.TOTAL;
	}

	// --------- Updating and retrieving formula -------

	updateRange(rangeData: DateRangeInterface.RangeInterface | null): void {
		if (!rangeData) {
			return;
		}

		const rangeTimeUnit = rangeData.timeUnit,
			rangeMultiplier = +rangeData.multiplier! || null,
			rangeOffset = rangeData.offset;

		const isRolling =
			rangeTimeUnit === TimeUnit.DAY &&
			!rangeData.startDate &&
			!rangeData.endDate &&
			// must not fit into last x days / last x days w/o current, or Tomorrow
			(Math.abs(Number(rangeOffset)) > 1 ||
				(rangeOffset === 1 && (rangeMultiplier ?? 0) > 1));

		let rangeStartDate: string | null | undefined, rangeEndDate: string | null | undefined;
		if (isRolling) {
			const momentObj = moment();

			momentObj.add(rangeOffset, 'day');
			rangeEndDate = momentObj.format(DateRange.customDateFormat);

			momentObj.subtract((rangeMultiplier ?? 0) - 1, 'day');
			rangeStartDate = momentObj.format(DateRange.customDateFormat);
		} else {
			rangeStartDate = rangeData.startDate;
			rangeEndDate = rangeData.endDate;
		}

		const props = {
			rangeTimeUnit,
			rangeMultiplier,
			rangeOffset,
			rangeIncludeFuture: !!rangeData.includeFuture,
			isAllTime: !!rangeData.isAllTime,
			isRolling,
			rangeStartDate,
			rangeEndDate,
		};

		(Object.keys(props) as (keyof typeof props)[]).forEach((key) => {
			this.updatePropIfChanged(key, props[key]);
		});

		this._rangeTitle = ''; // reset title
	}

	updateGranulation(granulationData: DateRangeInterface.GranulationInterface): void {
		if (!granulationData) {
			return;
		}

		const props: Partial<
			Pick<
				DateRange,
				| 'granulationTimeUnit'
				| 'granulationMultiplier'
				| 'granulationIsWholeRange'
				| 'granulationCumulative'
			>
		> = {};

		props.granulationTimeUnit = granulationData.timeUnit || null;
		props.granulationMultiplier = granulationData.multiplier || null;
		props.granulationIsWholeRange = !!granulationData.isWholeRange;

		// for non-total: set granulation only if it specified
		if (granulationData.isWholeRange) {
			props.granulationCumulative = false;
		} else if (typeof granulationData.cumulative !== 'undefined') {
			props.granulationCumulative = !!granulationData.cumulative;
		}

		(Object.keys(props) as (keyof typeof props)[]).forEach((key) => {
			this.updatePropIfChanged(key, props[key]);
		});
	}

	@cached
	get formula(): DateRangeInterface.FormulaInterface {
		const range = this.rangeFormula,
			granulation = this.granulationFormula;

		return { range, granulation };
	}

	set formula(formula: DateRangeInterface.FormulaInterface) {
		this.rangeFormula = formula.range;

		this.granulationFormula = formula.granulation!;
	}

	private _rangeFormula: DateRangeInterface.RangeInterface | null = null;

	// these can be used as a dependent prop to indicate change in date range output
	@cached
	get rangeFormula(): DateRangeInterface.RangeInterface {
		const rangeFormula = this._getRangeFormula();

		if (!equal(rangeFormula, this._rangeFormula)) {
			this._rangeFormula = rangeFormula;
		}
		return this._rangeFormula!;
	}

	set rangeFormula(value: DateRangeInterface.RangeInterface) {
		this.updateRange(value);
	}

	private _granulationFormula: DateRangeInterface.GranulationInterface | null = null;

	@cached
	get granulationFormula(): DateRangeInterface.GranulationInterface {
		const granulationFormula = this._getGranulationFormula();

		if (!equal(granulationFormula, this._granulationFormula)) {
			this._granulationFormula = granulationFormula;
		}
		return this._granulationFormula!;
	}

	set granulationFormula(value: DateRangeInterface.GranulationInterface) {
		this.updateGranulation(value);
	}

	get formulaId() {
		return getDateRangeIdFromFormula(this.formula);
	}

	// ------ private helpers for formula building ------

	_getRangeFormula(): DateRangeInterface.RangeInterface {
		const range: DateRangeInterface.RangeInterface = {};

		range.includeFuture = this.rangeIncludeFuture;
		range.isAllTime = this.isAllTime;

		range.startDate = null;
		range.endDate = null;

		if (this.rangeStartDate && this.rangeEndDate) {
			if (this.isRolling) {
				const momentObjStart = moment(this.rangeStartDate, DateRange.customDateFormat),
					momentObjEnd = moment(this.rangeEndDate, DateRange.customDateFormat);

				const daysDiff = Math.abs(
					moment.duration(momentObjEnd.diff(momentObjStart)).asDays(),
				);
				range.multiplier = Math.round(daysDiff) + 1;
				range.timeUnit = TimeUnit.DAY;

				const zeroOffset = moment()
					.startOf('day')
					.subtract(range.multiplier - 1, 'days');
				const offset = momentObjStart.diff(zeroOffset, 'days', true);

				range.offset = Math.sign(offset) * Math.round(Math.abs(offset));
			} else {
				// custom date range

				range.timeUnit = null;
				range.multiplier = null;
				range.offset = null;

				// stored as customDateFormat (YYYY-MM-DD)
				range.startDate = this.rangeStartDate as DateRangeInterface.IdDate;
				range.endDate = this.rangeEndDate as DateRangeInterface.IdDate;
			}
		} else if (this.rangeStartDate) {
			range.startDate = this.rangeStartDate as DateRangeInterface.IdDate;
			range.timeUnit = TimeUnit.DAY; // only til today / yesterday supported
			range.offset = this.rangeOffset;
			range.multiplier = null;
			range.includeFuture = true;
		} else {
			range.timeUnit = this.rangeTimeUnit;
			range.multiplier = +this.rangeMultiplier! || null;
			range.offset = this.rangeOffset;
		}

		return range;
	}

	_getGranulationFormula(): DateRangeInterface.GranulationInterface {
		if (this.granulationIsWholeRange) {
			return {
				timeUnit: null,
				multiplier: null,
				isWholeRange: true,
				cumulative: false,
			};
		}

		return {
			timeUnit: this.granulationTimeUnit,
			multiplier: this.granulationMultiplier,
			isWholeRange: false,
			cumulative: this.granulationCumulative,
		};
	}

	updateGranulationFromTypeId(granulationTypeId: GranulationTypeId): void {
		if (!granulationTypeId) {
			return;
		}

		const predefinedGranulation = DateRange.predefinedGranulations.find(
			(item) => item.id === granulationTypeId,
		);

		if (!predefinedGranulation) {
			this.loggerService.logError('[CDR] predefinedGranulation not found', {
				granulationTypeId,
			});
			return;
		}

		this.updateGranulation(predefinedGranulation.formula);
	}

	getOptimalGranulationsList(): GranulationTypeId[] {
		// rules: https://app.asana.com/0/1106834409426275/1169301938366081

		let optimalUnit;

		const rangeTimeUnit = this.rangeTimeUnit as number,
			rangeMultiplier = this.rangeMultiplier!;

		// ALLTIME - MONTHLY (tmp, perf issues)
		if (this.isAllTime) {
			optimalUnit = TimeUnit.MONTH;

			// HOURLY RULES
		} else if (
			rangeTimeUnit === TimeUnit.HOUR ||
			(rangeTimeUnit === TimeUnit.DAY && rangeMultiplier === 1) ||
			(this.isCustom && this.rangeStartDate === this.rangeEndDate)
		) {
			optimalUnit = TimeUnit.HOUR;

			// DAILY RULES
		} else if (
			(rangeTimeUnit === TimeUnit.DAY && rangeMultiplier > 1 && rangeMultiplier <= 180) ||
			(rangeTimeUnit === TimeUnit.WEEK && rangeMultiplier === 1) ||
			(rangeTimeUnit === TimeUnit.MONTH && rangeMultiplier === 1) ||
			(this.isCustom && this.rangeStartDate !== this.rangeEndDate) ||
			this.isSince
		) {
			optimalUnit = TimeUnit.DAY;

			// WEEKLY RULES
		} else if (
			rangeTimeUnit === TimeUnit.WEEK &&
			rangeMultiplier > 1 &&
			rangeMultiplier <= 420
		) {
			optimalUnit = TimeUnit.WEEK;

			// MONTHLY RULES
		} else if (
			(rangeTimeUnit === TimeUnit.DAY && rangeMultiplier > 180 && rangeMultiplier <= 366) ||
			(rangeTimeUnit === TimeUnit.MONTH && rangeMultiplier > 1 && rangeMultiplier <= 120) ||
			(rangeTimeUnit === TimeUnit.QUARTER && rangeMultiplier === 1) ||
			(rangeTimeUnit === TimeUnit.YEAR && rangeMultiplier === 1)
		) {
			optimalUnit = TimeUnit.MONTH;

			// QUARTERLY RULES
		} else if (rangeTimeUnit === TimeUnit.QUARTER && rangeMultiplier > 1) {
			optimalUnit = TimeUnit.QUARTER;

			// YEARLY RULES
		} else if (
			(rangeTimeUnit === TimeUnit.DAY && rangeMultiplier > 366) ||
			(rangeTimeUnit === TimeUnit.WEEK && rangeMultiplier > 420) ||
			(rangeTimeUnit === TimeUnit.MONTH && rangeMultiplier > 120) ||
			(rangeTimeUnit === TimeUnit.YEAR && rangeMultiplier > 1)
		) {
			optimalUnit = TimeUnit.YEAR;
		}

		if (!optimalUnit) {
			// don't log this for All Time
			if (!this.isAllTime && !DateRange.isRangeFormulaEmpty(this.rangeFormula)) {
				this.loggerService.logError('[CDR] Optimal granulation not found', {
					formula: JSON.stringify(this.formula),
				});
			}

			// fallback to day
			optimalUnit = TimeUnit.DAY;
		}

		// must return list of preferred, so we can check limitations: first go up, then down from the optimal
		const allUnits = [
				TimeUnit.HOUR,
				TimeUnit.DAY,
				TimeUnit.WEEK,
				TimeUnit.MONTH,
				TimeUnit.QUARTER,
				TimeUnit.YEAR,
			],
			optimalIndex = allUnits.indexOf(optimalUnit);

		const optimalUnitsList: TimeUnit[] = [];
		optimalUnitsList.push(...allUnits.slice(optimalIndex));
		optimalUnitsList.push(...allUnits.slice(0, optimalIndex).reverse());

		const optimalList = optimalUnitsList.map((timeUnit) => {
			return DateRange.predefinedGranulations.find((item) => item.unit === timeUnit)!.id;
		});

		return optimalList;
	}

	// --------- Utility fns -------

	getFormula(): DateRangeInterface.FormulaInterface {
		// use this to not accidently mutate the (cached) this.formula object
		return copyObject(this.formula);
	}

	updatePropIfChanged(propName: keyof this, propValue: unknown): void {
		// using underscorecard (cached) name only bcos if we compare with current
		// it throws "You attempted to update <bla bla> but it had already been used previously in the same computation"
		const cachedPropName = `_${String(propName)}` as keyof this;
		if (this[cachedPropName] !== propValue) {
			this[propName] = propValue as this[keyof this];
			this[cachedPropName] = propValue as this[keyof this];
		}
	}

	createCopy(): DateRange {
		const copy = DateRange.createFromFormula(this.formula);
		copy.rangeTitle = this.rangeTitle;
		copy.rangeCategoryId = this.rangeCategoryId;
		copy.rangeTypeIdVariations = this.rangeTypeIdVariations;

		return copy;
	}

	updateFromFormula(formula: DateRangeInterface.FormulaInterface): void {
		this.formula = formula;
	}

	updateFromPredefinedRangeId(rangeId: DateRangeSelectionId): void {
		const dateRange = DateRange.predefinedRanges.find(
			(item) => item.rangeUISelectionId === rangeId,
		);
		if (dateRange) {
			this.updateRange(dateRange.rangeFormula);
		}
	}

	updateFromPredefinedGranulationId(granId: GranulationTypeId): void {
		const granulation = DateRange.predefinedGranulations.find((item) => item.id === granId);
		if (granulation) {
			this.updateGranulation(granulation.formula);
		}
	}

	isEqual(withFormula: DateRangeInterface.FormulaInterface): boolean {
		return this.isRangeEqual(withFormula) && this.isGranulationEqual(withFormula);
	}

	isRangeEqual(withFormula: DateRangeInterface.FormulaInterface): boolean {
		const formula1 = this.formula,
			formula2 = DateRange.createFromFormula(withFormula).formula; // so we have all keys

		// compare with sorted keys
		// careful! only works with objects one layer deep
		const range1Sorted = JSON.stringify(
				formula1.range,
				Object.keys(formula1.range || {}).sort(),
			),
			range2Sorted = JSON.stringify(formula2.range, Object.keys(formula2.range || {}).sort());

		return range1Sorted === range2Sorted;
	}

	isGranulationEqual(withFormula: DateRangeInterface.FormulaInterface): boolean {
		const formula1 = this.formula,
			formula2 = DateRange.createFromFormula(withFormula).formula; // so we have all keys

		// compare with sorted keys
		// careful! only works with objects one layer deep
		const gran1Sorted = JSON.stringify(
				formula1.granulation,
				Object.keys(formula1.granulation || {}).sort(),
			),
			gran2Sorted = JSON.stringify(
				formula2.granulation,
				Object.keys(formula2.granulation || {}).sort(),
			);

		return gran1Sorted === gran2Sorted;
	}

	/**
	 * Selects the optimal granulation for this date range without considering metric limitations.
	 */
	selectOptimalGranulation(): void {
		const optimalGranulationTypeId = this.getOptimalGranulationsList()[0]!;
		this.updateGranulationFromTypeId(optimalGranulationTypeId);
	}

	@cached
	get hashCode(): string {
		return String(hashCode(JSON.stringify(this.getFormula())));
	}

	// ------- From limitations, used in selector (dropdowns) -------
	// these props are only used for those date-range that are created from predefined date ranges
	// and have limitations set by metric-limitations

	get isDisabled(): boolean {
		return (this.limitations || []).filter((item) => !item.disabled).length === 0;
	}

	get isDisabledForPaymentReasons(): boolean {
		return this.isDisabled && !!(this.limitations || []).find((item) => item.isPaidLimitation);
	}

	@cached
	get infoText(): string {
		let infoText: string[] = [];

		const limitations = this.limitations || [],
			isMultiple = limitations.length > 1;

		if (this.isDisabledForPaymentReasons) {
			const paidLimitation = limitations.find((item) => item.isPaidLimitation);
			if (paidLimitation) {
				infoText = [paidLimitation.infoText];
			}
		} else {
			// if all texts are the same, just show the text once
			if (
				(this.isDisabled || limitations.length === 1) &&
				uniq(limitations.map((item) => item.infoText)).length === 1
			) {
				infoText.push(limitations[0]!.infoText);
			} else {
				limitations.forEach((limitation) => {
					if (limitation.infoText) {
						let preText = '';
						if (isMultiple) {
							preText = `[${limitation.id}] `;
						}
						infoText.push(`${preText}${limitation.infoText}`);
					}
				});
			}
		}

		return infoText.join('<br>');
	}

	static predefinedRanges: DateRange[] = []; // initialized in createPredefinedRanges
	static predefinedGranulations: Granulation[] = []; // initialized in createPredefinedGranulations

	static Category = RangeCategory; // like enum

	static customDateFormat = 'YYYY-MM-DD';

	static rangeCategories = [
		{ id: RangeCategory.ALLTIME, title: 'All Time' },
		{ id: RangeCategory.POPULAR, title: 'Popular' },
		{ id: RangeCategory.CURRENT, title: 'Current' },
		{ id: RangeCategory.PREVIOUS, title: 'Previous' },
		{ id: RangeCategory.NEXT, title: 'Future' },
		{ id: RangeCategory.CUSTOM, title: 'Custom range' },
	];

	static createFromFormula(formula: DateRangeInterface.FormulaInterface): DateRange {
		const dateRange = new DateRange();
		dateRange.formula = formula;

		return dateRange;
	}

	static createFromRangeUISelectionId(rangeUISelectionId: DateRangeSelectionId): DateRange {
		let dateRange;

		const predefinedRange = this.predefinedRanges.find(
			(item) => item.rangeUISelectionId === rangeUISelectionId,
		);

		if (predefinedRange) {
			dateRange = predefinedRange.createCopy();
		} else {
			dateRange = this.predefinedRanges[0]!.createCopy();
		}

		return dateRange;
	}

	static createFromRangeUISelectionIdWithGranulationId(
		rangeUISelectionId: DateRangeSelectionId,
		granulationId: GranulationTypeId,
	): DateRange {
		const predefinedRange = this.predefinedRanges.find(
				(item) => item.rangeUISelectionId === rangeUISelectionId,
			)!,
			predefinedGranulation = this.predefinedGranulations.find(
				(item) => item.id === granulationId,
			)!;

		const dateRange = this.createFromFormula({
			range: predefinedRange.rangeFormula,
			granulation: predefinedGranulation.formula,
		});

		return dateRange;
	}

	static createLastX(
		rangeMultiplier: number,
		rangeTimeUnit: TimeUnit,
		granulationTypeId: GranulationTypeId,
		rangeIncludeCurrent = false,
	): DateRange {
		const dateRange = this.createFromRangeUISelectionIdWithGranulationId(
			DateRangeSelectionId.LAST_X,
			granulationTypeId,
		);
		dateRange.rangeMultiplier = rangeMultiplier;
		dateRange.rangeTimeUnit = rangeTimeUnit;
		dateRange.rangeOffset = rangeIncludeCurrent ? 0 : -1;
		return dateRange;
	}

	static baseGranulations = [
		// the `title`s are used in finding the correct limitation in cdr/range-list-item
		{ id: TimeUnit.TOTAL, title: 'Total' },
		{ id: TimeUnit.HOUR, title: 'Hour', titleShort: 'H' },
		{ id: TimeUnit.DAY, title: 'Day', titleShort: 'D' },
		{ id: TimeUnit.WEEK, title: 'Week', titleShort: 'W' },
		{ id: TimeUnit.MONTH, title: 'Month', titleShort: 'M' },
		{ id: TimeUnit.QUARTER, title: 'Quarter', titleShort: 'Q' },
		{ id: TimeUnit.YEAR, title: 'Year', titleShort: 'Y' },
	];

	static rangeFormulas:
		Record<DateRangeTypeId, PredefinedRangeFormulaInterface> | Record<string, never> = {}; // set from globals
	static granulationFormulas:
		Record<GranulationTypeId, PredefinedGranulationFormulaInterface> | Record<string, never> =
		{}; // set from globals

	// the formulas the server sends in coredata (BE's ids and enum values)
	static setFormulas(constants: Pick<Coredata.Constants, 'cdrRanges' | 'cdrGranulations'>): void {
		// read as RangeInterface: the LastX rules (strings) are only matched by type, and set before use
		this.rangeFormulas = constants.cdrRanges as typeof DateRange.rangeFormulas;
		this.granulationFormulas = constants.cdrGranulations;
	}

	static getPredefinedRangeFormula(rangeId: DateRangeTypeId): DateRangeInterface.RangeInterface {
		return this.rangeFormulas[rangeId]?.formula || {};
	}

	static getPredefinedGranulationFormula(
		rangeId: GranulationTypeId,
	): DateRangeInterface.GranulationInterface {
		return this.granulationFormulas[rangeId]?.formula || {};
	}

	static createPredefinedRanges(): void {
		// in correct order for showing on UI
		const predefined = [
			// all time
			{
				id: DateRangeSelectionId.ALL_TIME,
				title: 'All Time',
				categoryId: RangeCategory.ALLTIME,
			},

			// popular
			{
				id: DateRangeSelectionId.ALL_TIME,
				title: 'All Time',
				categoryId: RangeCategory.POPULAR,
			},
			{
				id: DateRangeSelectionId.WEEK_TO_DATE,
				title: 'Week to Date',
				categoryId: RangeCategory.POPULAR,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_WEEK,
				title: 'Last Week',
				categoryId: RangeCategory.POPULAR,
			},
			{
				id: DateRangeSelectionId.MONTH_TO_DATE,
				title: 'Month to Date',
				categoryId: RangeCategory.POPULAR,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_MONTH,
				title: 'Last Month',
				categoryId: RangeCategory.POPULAR,
			},

			// current
			{ id: DateRangeSelectionId.TODAY, title: 'Today', categoryId: RangeCategory.CURRENT },
			{
				id: DateRangeSelectionId.THIS_WEEK,
				title: 'This Week',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.THIS_MONTH,
				title: 'This Month',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.THIS_QUARTER,
				title: 'This Quarter',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.THIS_YEAR,
				title: 'This Year',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.WEEK_TO_DATE,
				title: 'Week to Date',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.MONTH_TO_DATE,
				title: 'Month to Date',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.QUARTER_TO_DATE,
				title: 'Quarter to Date',
				categoryId: RangeCategory.CURRENT,
			},
			{
				id: DateRangeSelectionId.YEAR_TO_DATE,
				title: 'Year to Date',
				categoryId: RangeCategory.CURRENT,
			},

			// previous
			{
				id: DateRangeSelectionId.YESTERDAY,
				title: 'Yesterday',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_WEEK,
				title: 'Last Week',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_MONTH,
				title: 'Last Month',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_QUARTER,
				title: 'Last Quarter',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.PREVIOUS_YEAR,
				title: 'Last Year',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.LAST_X,
				title: 'Last...',
				categoryId: RangeCategory.PREVIOUS,
			},
			{
				id: DateRangeSelectionId.SINCE_TO_DATE,
				title: 'Since...',
				categoryId: RangeCategory.PREVIOUS,
			},

			// next
			{
				id: DateRangeSelectionId.TOMORROW,
				title: 'Tomorrow',
				categoryId: RangeCategory.NEXT,
			},
			{
				id: DateRangeSelectionId.NEXT_WEEK,
				title: 'Next Week',
				categoryId: RangeCategory.NEXT,
			},
			{
				id: DateRangeSelectionId.NEXT_MONTH,
				title: 'Next Month',
				categoryId: RangeCategory.NEXT,
			},
			{
				id: DateRangeSelectionId.NEXT_QUARTER,
				title: 'Next Quarter',
				categoryId: RangeCategory.NEXT,
			},
			{
				id: DateRangeSelectionId.NEXT_YEAR,
				title: 'Next Year',
				categoryId: RangeCategory.NEXT,
			},

			// custom range
			{
				id: DateRangeSelectionId.CUSTOM,
				title: 'Custom range',
				categoryId: RangeCategory.CUSTOM,
			},
		];

		const ranges: DateRange[] = [];

		predefined.forEach((pre) => {
			let formula;
			const rangeTypeIdVariations: DateRangeTypeId[] = []; // needed to show dynamic limitations for different types

			if (pre.id === DateRangeSelectionId.TODAY) {
				formula = this.getPredefinedRangeFormula(DateRangeTypeId.TODAY);
				rangeTypeIdVariations.push(DateRangeTypeId.TODAY, DateRangeTypeId.TODAY_TO_HOUR);
			} else if (pre.id === DateRangeSelectionId.LAST_X) {
				formula = Object.assign(
					{},
					this.getPredefinedRangeFormula(DateRangeTypeId.LAST_X_DAYS_WOC),
				);
				formula.multiplier = 30;
				rangeTypeIdVariations.push(
					DateRangeTypeId.LAST_X_DAYS,
					DateRangeTypeId.LAST_X_DAYS_WOC,
				);
				rangeTypeIdVariations.push(
					DateRangeTypeId.LAST_X_WEEKS,
					DateRangeTypeId.LAST_X_WEEKS_WOC,
				);
				rangeTypeIdVariations.push(
					DateRangeTypeId.LAST_X_MONTHS,
					DateRangeTypeId.LAST_X_MONTHS_WOC,
				);
				rangeTypeIdVariations.push(
					DateRangeTypeId.LAST_X_QUARTERS,
					DateRangeTypeId.LAST_X_QUARTERS_WOC,
				);
				rangeTypeIdVariations.push(
					DateRangeTypeId.LAST_X_YEARS,
					DateRangeTypeId.LAST_X_YEARS_WOC,
				);
			} else if (pre.id === DateRangeSelectionId.SINCE_TO_DATE) {
				formula = Object.assign(
					{},
					this.getPredefinedRangeFormula(DateRangeTypeId.LAST_X_DAYS_WOC),
				);
				formula.startDate = moment()
					.startOf('month')
					.format(this.customDateFormat) as DateRangeInterface.IdDate;
				formula.multiplier = null;
				rangeTypeIdVariations.push(
					DateRangeTypeId.SINCE_TO_DATE,
					DateRangeTypeId.SINCE_TO_DATE_WOC,
				);
			} else if (pre.id === DateRangeSelectionId.CUSTOM) {
				// // default is absolute range
				// formula.startDate = '2019-09-01';
				// formula.endDate = '2019-09-27';

				// default is showing last 14 days, from 2 days ago - rolling
				formula = Object.assign(
					{},
					this.getPredefinedRangeFormula(DateRangeTypeId.LAST_X_DAYS_ROLLING),
				);
				formula.offset = -2;
				formula.multiplier = 14;

				formula.includeFuture = true;
				rangeTypeIdVariations.push(
					DateRangeTypeId.CUSTOM,
					DateRangeTypeId.LAST_X_DAYS_ROLLING,
				);
			} else {
				// the rest of ids must match
				const rangeTypeId = pre.id as string as DateRangeTypeId; // same string values
				formula = this.getPredefinedRangeFormula(rangeTypeId);
				rangeTypeIdVariations.push(rangeTypeId);
			}

			const dateRange = this.createFromFormula({ range: formula });

			// dateRange.rangeUISelectionId = pre.id;
			dateRange.rangeTitle = pre.title;
			dateRange.rangeCategoryId = pre.categoryId;
			dateRange.rangeTypeIdVariations = rangeTypeIdVariations;

			ranges.push(dateRange);
		});

		this.predefinedRanges = ranges;
	}

	static createPredefinedGranulations(): void {
		const granulations = [
			// 'unit' is used to filter out certain types
			{ id: GranulationTypeId.TOTAL, title: 'Total', unit: TimeUnit.TOTAL },
			{ id: GranulationTypeId.HOURLY, title: 'Hour', unit: TimeUnit.HOUR },
			// {id: GranulationTypeId.HOURLY_2, title: '2 Hours', unit: TimeUnit.HOUR},
			// {id: GranulationTypeId.HOURLY_6, title: '6 Hours', unit: TimeUnit.HOUR},
			// {id: GranulationTypeId.HOURLY_12, title: '12 Hours', unit: TimeUnit.HOUR},
			{ id: GranulationTypeId.DAILY, title: 'Day', unit: TimeUnit.DAY },
			{ id: GranulationTypeId.WEEKLY, title: 'Week', unit: TimeUnit.WEEK },
			{ id: GranulationTypeId.MONTHLY, title: 'Month', unit: TimeUnit.MONTH },
			{ id: GranulationTypeId.QUARTERLY, title: 'Quarter', unit: TimeUnit.QUARTER },
			{ id: GranulationTypeId.YEARLY, title: 'Year', unit: TimeUnit.YEAR },
		];

		const predefinedGranulations: Granulation[] = [];

		granulations.map((granulation) => {
			let formula;

			try {
				formula = this.getPredefinedGranulationFormula(granulation.id);
			} catch (_e) {
				return;
			}

			predefinedGranulations.push({
				id: granulation.id,
				title: granulation.title,
				unit: granulation.unit,
				formula,
			});
		});

		this.predefinedGranulations = predefinedGranulations;
	}

	static getTimeUnitText(timeUnit: TimeUnit, withLyPostfix = false, keepCapital = false) {
		let title = this.baseGranulations.find((item) => item.id === timeUnit)?.title;
		if (!title) {
			return 'Unknown';
		}
		if (withLyPostfix) {
			title = timeUnit === TimeUnit.DAY ? 'Daily' : `${title}ly`;
		}
		if (!keepCapital) {
			title = title.toLowerCase();
		}
		return title;
	}

	static isRangeFormulaEmpty(
		range: DateRangeInterface.RangeInterface | null | undefined,
	): boolean {
		// skip logging errors for such formulas that are obviously "nothing selected"

		if (!range) {
			return true;
		}
		return (
			!range.includeFuture &&
			!range.isAllTime &&
			!range.startDate &&
			!range.endDate &&
			!range.timeUnit &&
			!range.multiplier &&
			!range.offset
		);
	}

	static getSelectionIdFromTypeId(typeId: DateRangeTypeId): DateRangeSelectionId {
		if (typeId.includes('LastX')) {
			return DateRangeSelectionId.LAST_X;
		} else if (
			typeId === DateRangeTypeId.CUSTOM ||
			typeId === DateRangeTypeId.LAST_X_DAYS_ROLLING
		) {
			return DateRangeSelectionId.CUSTOM;
		} else if (
			typeId === DateRangeTypeId.SINCE_TO_DATE ||
			typeId === DateRangeTypeId.SINCE_TO_DATE_WOC
		) {
			return DateRangeSelectionId.SINCE_TO_DATE;
		} else if (typeId === DateRangeTypeId.TODAY_TO_HOUR) {
			return DateRangeSelectionId.TODAY;
		} else {
			// For most cases, the DateRangeTypeId maps directly to DateRangeSelectionId
			return typeId as string as DateRangeSelectionId;
		}
	}

	static getOptimalSelectionId(): DateRangeSelectionId {
		const optimalTypeId = this.optimalOrder[0]!;
		return this.getSelectionIdFromTypeId(optimalTypeId);
	}

	static optimalOrder = [
		// same as somewhere on BE
		DateRangeTypeId.MONTH_TO_DATE,
		DateRangeTypeId.WEEK_TO_DATE,
		DateRangeTypeId.THIS_WEEK,
		DateRangeTypeId.THIS_MONTH,
		DateRangeTypeId.PREVIOUS_WEEK,
		DateRangeTypeId.PREVIOUS_MONTH,
		DateRangeTypeId.LAST_X_DAYS,
		DateRangeTypeId.LAST_X_DAYS_WOC,
		DateRangeTypeId.LAST_X_DAYS_ROLLING,
		DateRangeTypeId.LAST_X_WEEKS,
		DateRangeTypeId.LAST_X_WEEKS_WOC,
		DateRangeTypeId.LAST_X_MONTHS,
		DateRangeTypeId.LAST_X_MONTHS_WOC,
		DateRangeTypeId.ALL_TIME,
		DateRangeTypeId.TODAY,
		DateRangeTypeId.THIS_QUARTER,
		DateRangeTypeId.THIS_YEAR,
		DateRangeTypeId.TODAY_TO_HOUR,
		DateRangeTypeId.QUARTER_TO_DATE,
		DateRangeTypeId.YEAR_TO_DATE,
		DateRangeTypeId.YESTERDAY,
		DateRangeTypeId.PREVIOUS_QUARTER,
		DateRangeTypeId.PREVIOUS_YEAR,
		DateRangeTypeId.LAST_X_QUARTERS,
		DateRangeTypeId.LAST_X_QUARTERS_WOC,
		DateRangeTypeId.LAST_X_YEARS,
		DateRangeTypeId.LAST_X_YEARS_WOC,
		DateRangeTypeId.LAST_X_HOURS,
		DateRangeTypeId.LAST_X_HOURS_WOC,
		DateRangeTypeId.TOMORROW,
		DateRangeTypeId.NEXT_WEEK,
		DateRangeTypeId.NEXT_MONTH,
		DateRangeTypeId.NEXT_QUARTER,
		DateRangeTypeId.NEXT_YEAR,
	];
}
